import {
  Event,
  EventType,
  MediaAspectRatio,
  ThemeType,
} from "@markit/common.types";
import { useMemo, useState, useRef, ReactNode } from "react";
import { Icon } from "@iconify/react";
import { Colors } from "../../../../utils/colors";
import ReactPlayer from "react-player";
import { isDesktop } from "react-device-detect";
import { useOnMount } from "../../../../hooks/useOnMount";
import { useTheme } from "../../../../hooks/useTheme";
import { getCanonicalMediaUrls } from "@markit/common.utils";
import MediaCarouselContainer from "../../../Media/MediaCarouselContainer";
import MediaExpandedItem from "../../../Media/MediaExpandedItem";
import MediaAspectRatioDropdown from "./MediaAspectRatioDropdown";
import { isPortraitAspectRatio } from "../../../../utils/mediaUtils";

type MediaPlayerProps = {
  productData: Event;
  setProductData?: (productData: Event) => void; // for creation side
  selectedMediaIndex: number;
  setSelectedMediaIndex: (index: number) => void;
  creatorName: string;
  blurring?: boolean; // Whether to blur the video
  allowAutoplay?: boolean; // Whether to start playing the video automatically
  hardTheme: ThemeType;
  renderMediaPreviewCarousel?: ReactNode;
};

// Helper component for displaying a media player with aspect ratio dropdown - used for video, audio, and image
const MediaPlayer = (props: MediaPlayerProps) => {
  const {
    productData,
    setProductData,
    selectedMediaIndex,
    setSelectedMediaIndex,
    creatorName,
    blurring,
    allowAutoplay,
    hardTheme,
    renderMediaPreviewCarousel,
  } = props;
  const { theme } = useTheme(hardTheme);

  const [playing, setPlaying] = useState(false);
  const [expandedImageUrl, setExpandedImageUrl] = useState("");
  const hasAutoplayedRef = useRef(false);

  const styles = {
    overlayButtonContainer: {
      top: 5,
      right: 5,
      zIndex: 10,
      gap: 7,
    },
    overlayButton: {
      backgroundColor: Colors.WHITE1,
      border: `0.5px solid ${Colors.GRAY2}`,
      borderRadius: 8,
      height: 35,
      width: 35,
      cursor: "pointer",
    },
  };

  const mediaUrls = useMemo(
    () => getCanonicalMediaUrls(productData),
    [productData]
  );

  const selectedMediaUrl = useMemo(
    () => mediaUrls[selectedMediaIndex] || "",
    [mediaUrls, selectedMediaIndex]
  );

  const isAudioPlayer = useMemo(
    () => productData.eventType === EventType.AUDIO,
    [productData.eventType]
  );

  const isCustomProduct = useMemo(
    () => productData.eventType === EventType.CUSTOM_PRODUCT,
    [productData.eventType]
  );

  const playerWidth = isDesktop
    ? isPortraitAspectRatio(productData.mediaAspectRatio)
      ? 500
      : 700
    : "94vw";
  const playerMaxWidth = isDesktop ? 800 : "100%";

  useOnMount(() => {
    // Autoplay only for videos and not for audio. This is because the media has to start muted to autoplay, which we don't want for audio.
    if (
      allowAutoplay &&
      !isAudioPlayer &&
      !hasAutoplayedRef.current &&
      selectedMediaUrl
    ) {
      // Small delay to ensure ReactPlayer is ready
      const timer = setTimeout(() => {
        setPlaying(true);
        hasAutoplayedRef.current = true;
      }, 100);
      return () => clearTimeout(timer);
    }
  });

  return (
    <>
      {expandedImageUrl ? (
        <MediaExpandedItem
          mediaUrl={expandedImageUrl}
          onClose={() => setExpandedImageUrl("")}
          hardTheme={hardTheme}
        />
      ) : null}
      <div className="Centering" style={{ width: "100%" }}>
        <div
          className="ColumnCentering"
          style={{
            width: playerWidth,
            maxWidth: playerMaxWidth,
            gap: 14,
          }}
        >
          <div
            className="Centering"
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "100%",
              backgroundColor: isAudioPlayer
                ? Colors.GRAY10
                : hardTheme === ThemeType.Dark
                  ? Colors.BLACK
                  : Colors.WHITE,
              borderRadius: 12,
              border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
              padding: isAudioPlayer && isDesktop ? 10 : 0,
              overflow: "hidden",
              aspectRatio: isAudioPlayer
                ? "16/9"
                : productData.mediaAspectRatio || "16/9",
              filter: blurring ? "blur(6px) brightness(40%)" : "none",
              pointerEvents: blurring ? "none" : "auto",
            }}
          >
            {productData.eventType === EventType.CUSTOM_PRODUCT ? (
              <MediaCarouselContainer
                event={productData}
                selectedMediaIndex={selectedMediaIndex}
                setSelectedMediaIndex={setSelectedMediaIndex}
                imageStyle={{
                  width: "100%",
                  height: "100%",
                  aspectRatio: productData.mediaAspectRatio || "16/9",
                  cursor: !setProductData ? "pointer" : "auto",
                }}
                onImageClick={() =>
                  !setProductData
                    ? setExpandedImageUrl(mediaUrls[selectedMediaIndex])
                    : undefined
                }
              />
            ) : (
              <ReactPlayer
                src={selectedMediaUrl}
                width="100%"
                height="100%"
                controls={true}
                light={false}
                playing={playing}
                muted={allowAutoplay && !isAudioPlayer}
                playsInline={!isDesktop}
                onPlay={() => {
                  // Sync state when user clicks play
                  if (!playing) {
                    setPlaying(true);
                  }
                }}
                onPause={() => {
                  // Sync state when video pauses
                  if (playing) {
                    setPlaying(false);
                  }
                }}
              />
            )}
            {isAudioPlayer ? (
              <div
                className="AlignedRow"
                style={{
                  position: "absolute",
                  left: 10,
                  top: 10,
                  zIndex: 10,
                  gap: 10,
                }}
              >
                <div
                  className="Centering"
                  style={{
                    width: isDesktop ? 80 : 50,
                    height: isDesktop ? 80 : 50,
                    borderRadius: 8,
                    backgroundColor: Colors.GRAY7,
                  }}
                >
                  <Icon
                    icon="mdi:keyboard-voice"
                    height={isDesktop ? 60 : 30}
                    color={Colors.WHITE}
                  />
                </div>
                <div className="ColumnNormal" style={{ gap: 4 }}>
                  <span
                    className={isDesktop ? "bodySubtext" : "smallBodySubtext"}
                  >
                    {creatorName}
                  </span>
                  <span
                    className={isDesktop ? "sectionTitle" : "bodyMedium"}
                    style={{ color: Colors.WHITE }}
                  >
                    {productData.title || "Untitled"}
                  </span>
                </div>
              </div>
            ) : null}
            {setProductData ? (
              <div
                className="ColumnNormal"
                style={{
                  position: "absolute",
                  ...styles.overlayButtonContainer,
                }}
              >
                {mediaUrls.length === 1 ? (
                  <div
                    className="Centering"
                    style={{ ...styles.overlayButton }}
                    onClick={() => {
                      setProductData({
                        ...productData,
                        mediaURLs: [],
                        mediaURL: "", // backsupport
                        mediaAspectRatio: MediaAspectRatio.NONE,
                        photoURL: "",
                      });
                    }}
                  >
                    <Icon icon="ion:close" height={20} color={Colors.BLACK} />
                  </div>
                ) : null}
                {!isAudioPlayer ? (
                  <MediaAspectRatioDropdown
                    productData={productData}
                    setProductData={setProductData}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
          {isCustomProduct &&
          mediaUrls.length > (setProductData ? 0 : 1) &&
          (isDesktop || setProductData) ? (
            <div style={{ width: "100%" }}>{renderMediaPreviewCarousel}</div>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default MediaPlayer;
